import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChatConversationDetailSchema,
  ChatMessageSchema,
  type ChatConversationDetailDto,
  type ChatMessageDto,
} from '@taqon/contracts';
import { z } from 'zod';
import { patchJson, postJson } from '../../shared/api/client';

/**
 * Internal chat API helpers (FRD §4.9; SDD §29.9). Delivery is by polling, the existing TanStack
 * Query pattern: no server push in Release 1 (TD-54). The intervals are here, in one place, so they
 * can be tuned if chat polling ever meets the API rate limit (SDD §29.9 revisit trigger).
 */
export const CHAT = '/api/v1/chat';
export const CHAT_GROUPS = '/api/v1/chat/groups';
export const CHAT_KEY = ['chat'] as const;
export const CHAT_GROUPS_KEY = ['chat', 'groups'] as const;

/**
 * The open conversation, while the tab is visible. 10 s, not 5 s: an open chat measured about 0.47
 * API requests per second at 5 s, so many users behind one office address could reach the API rate
 * limit (SDD §29.9; approved 29 September 2026).
 */
export const OPEN_CONVERSATION_POLL_MS = 10_000;
/** The conversation list and the unread count. */
export const CONVERSATION_LIST_POLL_MS = 30_000;
/** Messages loaded at a time. */
export const MESSAGE_PAGE = 50;

export const conversationsKey = [...CHAT_KEY, 'conversations'] as const;
export const unreadKey = [...CHAT_KEY, 'unread'] as const;
export const conversationKey = (id: string) => [...CHAT_KEY, 'conversation', id] as const;

const ReadResultSchema = z.object({ read: z.literal(true) });

/** The caller's own chat actions, each refreshing what it changes. */
export function useChatActions(csrfToken: string) {
  const queryClient = useQueryClient();
  const refreshList = () => queryClient.invalidateQueries({ queryKey: CHAT_KEY });

  const start = useMutation({
    mutationFn: (userId: string): Promise<ChatConversationDetailDto> =>
      postJson(
        `${CHAT}/conversations/one-to-one`,
        { userId },
        ChatConversationDetailSchema,
        csrfToken,
      ),
    onSuccess: refreshList,
  });
  const post = useMutation({
    mutationFn: (input: {
      conversationId: string;
      body: string;
      mentionUserIds: readonly string[];
    }): Promise<ChatMessageDto> =>
      postJson(
        `${CHAT}/conversations/${encodeURIComponent(input.conversationId)}/messages`,
        { body: input.body, mentionUserIds: input.mentionUserIds },
        ChatMessageSchema,
        csrfToken,
      ),
  });
  const edit = useMutation({
    mutationFn: (input: {
      message: ChatMessageDto;
      body: string;
      mentionUserIds: readonly string[];
    }): Promise<ChatMessageDto> =>
      patchJson(
        `${CHAT}/messages/${encodeURIComponent(input.message.id)}`,
        { body: input.body, mentionUserIds: input.mentionUserIds },
        ChatMessageSchema,
        csrfToken,
        input.message.version,
      ),
  });
  const remove = useMutation({
    mutationFn: (message: ChatMessageDto): Promise<ChatMessageDto> =>
      postJson(
        `${CHAT}/messages/${encodeURIComponent(message.id)}/delete`,
        {},
        ChatMessageSchema,
        csrfToken,
      ),
  });
  const markRead = useMutation({
    mutationFn: (input: { conversationId: string; lastMessageId: string }) =>
      postJson(
        `${CHAT}/conversations/${encodeURIComponent(input.conversationId)}/read`,
        { lastMessageId: input.lastMessageId },
        ReadResultSchema,
        csrfToken,
      ),
    onSuccess: refreshList,
  });
  return { start, post, edit, remove, markRead };
}

/** How a message that can no longer be read is shown. */
export function placeholderFor(message: ChatMessageDto): string | null {
  if (message.status === 'deleted') return 'This message was deleted.';
  if (message.status === 'removed') return 'This message was removed by a moderator.';
  return null;
}

/** Messages in display order (oldest first), merged by id without duplicates. */
export function mergeMessages(
  current: readonly ChatMessageDto[],
  incoming: readonly ChatMessageDto[],
): ChatMessageDto[] {
  const byId = new Map<string, ChatMessageDto>();
  for (const message of current) byId.set(message.id, message);
  for (const message of incoming) byId.set(message.id, message);
  return [...byId.values()].sort((a, b) => compareIds(a.id, b.id));
}

function compareIds(a: string, b: string): number {
  return a.length === b.length ? a.localeCompare(b) : a.length - b.length;
}
